/**
 * AVA Design System — mapeos de status de negocio a tone del DS
 *
 * Helpers que convierten enums del backend (RequestStatus, DocumentStatus,
 * RiskLevel, DecisionType) al tone semántico del DS (success/warn/info/danger/neutral).
 *
 * Motivo: hoy hay 6+ funciones `getStatusBadge` duplicadas en distintos
 * archivos (ComplianceRequestDetails, ComplianceOfficerDashboard, Home,
 * DocumentCard, etc.) con variaciones leves. Centralizando acá:
 *  - 1 sola decisión por mapeo (más fácil cambiar)
 *  - consistencia entre todas las pantallas
 *  - tests unitarios cubren la lógica de mapeo
 *
 * Uso:
 *   <StatusBadge tone={requestStatusToTone(request.status)}>
 *     {requestStatusToLabel(request.status)}
 *   </StatusBadge>
 */
import type { StatusTone } from "./StatusBadge";

// =========================================================
// Onboarding request status
// =========================================================
// V008 (Sprint Lifecycle v2): `DOCUMENTS_PENDING` fue removido del enum.
// El request permanece en `PROCESSING` hasta la decisión final.
export type RequestStatusValue =
  | "DRAFT"
  | "SUBMITTED"
  | "PENDING_REVIEW"
  | "IN_REVIEW"
  | "PROCESSING"
  | "COMPLETED"
  | "APPROVED"
  | "REJECTED"
  | "AUTO_REJECTED"
  | "OBSERVED"
  | "EXPIRED"
  | "CANCELLED";

export function requestStatusToTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case "APPROVED":
    case "COMPLETED":
      return "success";
    case "REJECTED":
    case "AUTO_REJECTED":
      return "danger";
    case "OBSERVED":
    case "PENDING_REVIEW":
      return "warn";
    case "SUBMITTED":
    case "PROCESSING":
    case "IN_REVIEW":
      return "info";
    case "EXPIRED":
    case "CANCELLED":
    case "DRAFT":
    default:
      return "neutral";
  }
}

export function requestStatusToLabel(status: string | null | undefined): string {
  switch (status) {
    case "DRAFT": return "Borrador";
    case "SUBMITTED": return "Enviado";
    case "PENDING_REVIEW": return "Pendiente de revisión";
    case "IN_REVIEW": return "En revisión";
    case "PROCESSING": return "En proceso";
    case "COMPLETED":
    case "APPROVED":
      return "Aprobada";
    case "REJECTED": return "Rechazada";
    case "AUTO_REJECTED": return "Rechazada (auto)";
    case "OBSERVED": return "En observación";
    case "EXPIRED": return "Expirada";
    case "CANCELLED": return "Cancelada";
    default: return status || "Sin estado";
  }
}

// =========================================================
// Document status (extracción + revisión de compliance)
// =========================================================
export type DocumentStatusValue =
  | "PENDING"
  | "PROCESSING"
  | "VERIFICATION_PENDING"
  | "OK"
  | "ERROR"
  | "INCORRECT"
  | "REPLACED"
  | "REJECTED";

export function documentStatusToTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case "OK":
      return "success";
    case "REJECTED":
    case "ERROR":
    case "INCORRECT":
      return "danger";
    case "PENDING":
    case "VERIFICATION_PENDING":
      return "warn";
    case "PROCESSING":
      return "info";
    case "REPLACED":
    default:
      return "neutral";
  }
}

export function documentStatusToLabel(status: string | null | undefined): string {
  switch (status) {
    case "PENDING": return "Pendiente";
    case "PROCESSING": return "Procesando";
    case "VERIFICATION_PENDING": return "Verificando";
    case "OK": return "Validado";
    case "ERROR": return "Error";
    case "INCORRECT": return "Tipo incorrecto";
    case "REPLACED": return "Reemplazado";
    case "REJECTED": return "Rechazado";
    default: return status || "Sin estado";
  }
}

// =========================================================
// Risk level (matriz de riesgo KYC/AML)
// =========================================================
export type RiskLevelValue = "BAJO" | "MEDIO" | "ALTO" | "MUY_ALTO" | "CRITICO";

/**
 * Nivel de riesgo → tone del DS. FUENTE ÚNICA del mapeo nivel→color en client y
 * compliance (TALAPP-1933): header del caso, dashboard, Resumen (score, escala,
 * matriz), ayuda y KYC delegan acá. Escala monotónica de TALAPP-1887, a peor
 * nivel color más alarmante: BAJO success (verde) · MEDIO info (azul) · ALTO
 * warn (naranja) · CRITICO / MUY_ALTO danger (rojo). Nivel desconocido →
 * neutral (los consumidores que necesitan fail-closed lo tratan como danger).
 */
export function riskLevelToTone(level: string | null | undefined): StatusTone {
  switch (level) {
    case "BAJO":
      return "success";
    case "MEDIO":
      return "info";
    case "ALTO":
      return "warn";
    case "MUY_ALTO":
    case "CRITICO":
      return "danger";
    default:
      return "neutral";
  }
}

export function riskLevelToLabel(level: string | null | undefined): string {
  switch (level) {
    case "BAJO": return "Riesgo bajo";
    case "MEDIO": return "Riesgo medio";
    case "ALTO": return "Riesgo alto";
    case "MUY_ALTO": return "Riesgo muy alto";
    case "CRITICO": return "Riesgo crítico";
    default: return level || "Sin clasificar";
  }
}

// =========================================================
// Decision (compliance officer)
// =========================================================
export type DecisionValue = "APROBAR" | "OBSERVAR" | "RECHAZAR" | "OBSERVADO";

export function decisionToTone(decision: string | null | undefined): StatusTone {
  switch (decision) {
    case "APROBAR":
      return "success";
    case "RECHAZAR":
      return "danger";
    case "OBSERVAR":
    case "OBSERVADO":
      return "warn";
    default:
      return "neutral";
  }
}

export function decisionToLabel(decision: string | null | undefined): string {
  switch (decision) {
    case "APROBAR": return "Aprobada";
    case "RECHAZAR": return "Rechazada";
    case "OBSERVAR":
    case "OBSERVADO":
      return "En observación";
    default: return decision || "Sin decisión";
  }
}

// =========================================================
// Additional document request status
// =========================================================
export type AdditionalDocStatusValue = "PENDIENTE" | "RECIBIDO" | "VALIDADO" | "RECHAZADO";

export function additionalDocStatusToTone(estado: string | null | undefined): StatusTone {
  switch (estado) {
    case "VALIDADO":
      return "success";
    case "RECIBIDO":
      return "info";
    case "RECHAZADO":
      return "danger";
    case "PENDIENTE":
    default:
      return "warn";
  }
}

export function additionalDocStatusToLabel(estado: string | null | undefined): string {
  switch (estado) {
    case "PENDIENTE": return "Pendiente";
    case "RECIBIDO": return "Recibido";
    case "VALIDADO": return "Validado";
    case "RECHAZADO": return "Rechazado";
    default: return estado || "Sin estado";
  }
}
